/* newbrand-cine-brand -- the behaviour of the house chapters (05-10).

   Moved out of the section on 2026-09-22 (mobile audit P1.1). It used to be an
   inline IIFE in every chapter, keyed on that chapter's #cine-<section id>;
   six houses on the homepage meant six copies. Now each chapter includes this
   file (deferred -- the browser fetches it once) and the first run sets up
   every [data-cine-brand] root on the page; later copies stop at the guard.

   Theme editor: shopify:section:load sets up the re-rendered chapter and
   shopify:section:unload takes down the one going away (its window listeners
   and observer, which would otherwise outlive it).

   What the script does is unchanged:
   - fitLogo sizes the mark from the chapter's leftover height;
   - the chips and the product rail stay in step -- the lit chip is the tab
     that owns the leftmost visible card, and tapping a chip scrolls the rail
     to that tab's first card. */
(function () {
  if (window.__nbCineBrand) return;
  window.__nbCineBrand = true;

  function setup(root) {
    if (root.__nbCineBrand) return;
    var off = [];
    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      off.push(function () { target.removeEventListener(type, fn, opts); });
    }
    root.__nbCineBrand = {
      destroy: function () {
        off.forEach(function (f) { f(); });
        off = [];
        root.__nbCineBrand = null;
      }
    };

    var track = root.querySelector('[data-cine-track]');
    /* The mark is sized from what is actually left over, not from a table of
       breakpoints. The chapter is exactly one viewport; whatever the card, the
       tab strip, the product rail and the link do not use is slack, and the
       slack varies by house (three tabs or eight, one line of blurb or two)
       and by phone. Measuring it once the layout has settled is the only way
       to fill it without overflowing on the short cases the breakpoints were
       guarding. */
    var logo = root.querySelector('.cine-blogo');
    function fitLogo() {
      if (!logo) return;
      logo.style.height = '';
      var floor = logo.getBoundingClientRect().height;
      if (!floor) return;
      var col = root.querySelector('.cine-col');
      if (!col || !col.clientHeight) return;
      // The column is `flex: 1 1 auto`, so it always stretches to the full
      // chapter -- measuring it against the chapter's own box reports zero slack
      // even when the screen is visibly half empty. What is actually free is the
      // difference between the column and the real extent of the things in it.
      var parts = ['.cine-bcard', '.cine-btabs', '[data-cine-track]', '.cine-blink']
        .map(function (sel) { return root.querySelector(sel); })
        .filter(function (el) { return el && el.getBoundingClientRect().height > 0; })
        .map(function (el) { return el.getBoundingClientRect(); });
      if (!parts.length) return;
      var top = Math.min.apply(null, parts.map(function (r) { return r.top; }));
      var bottom = Math.max.apply(null, parts.map(function (r) { return r.bottom; }));
      var slack = col.clientHeight - (bottom - top);
      var target = floor;
      if (slack > 10) {
        var cap = parseFloat(getComputedStyle(root).getPropertyValue('--cine-lmax')) || 220;
        var ceiling = Math.min(root.clientHeight * 0.34, cap);
        target = Math.min(floor + slack - 10, ceiling);
      }
      // The house trim is applied last, to whatever the fit arrived at, so the
      // difference it makes is the same proportion on every screen size. What
      // it gives up becomes empty space around the mark rather than going to
      // anything else on the chapter.
      var scale = parseFloat(getComputedStyle(root).getPropertyValue('--cine-lsc')) || 1;
      if (scale !== 1 || target > floor + 2) {
        logo.style.height = Math.round(target * scale) + 'px';
      }
    }
    if (logo) {
      fitLogo();
      if (logo.complete) fitLogo(); else on(logo, 'load', fitLogo);
      on(window, 'resize', fitLogo, { passive: true });
      on(window, 'orientationchange', fitLogo);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (root.__nbCineBrand) fitLogo(); });
      // the mark arrives with the chapter, so re-fit when its src is filled in
      var mo = new MutationObserver(fitLogo);
      mo.observe(logo, { attributes: true, attributeFilter: ['src'] });
      off.push(function () { mo.disconnect(); });
      // A chapter off screen may have no layout at all to measure -- content
      // visibility skips it -- so the fit is redone when the deck brings it on.
      on(document, 'cine:chapter', function (e) {
        if (e.detail && e.detail.el === root) requestAnimationFrame(fitLogo);
      });
    }

    var tabRow = root.querySelector('[data-cine-tabs]');
    if (!track) return;

    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-cine-tab]'));
    var chips = tabRow ? Array.prototype.slice.call(tabRow.querySelectorAll('[data-cine-tabfor]')) : [];
    if (!cards.length || !chips.length) return;

    // Bring a chip into view when the rail has moved on to its products. It
    // only nudges when the chip is actually off the edge, so the strip is not
    // dragged along under the finger on every frame -- that was the complaint
    // the first time round, when the tab also switched before its products ran
    // out.
    function ensureChipVisible(n) {
      var c = chips[n];
      if (!c) return;
      var left = c.offsetLeft, right = left + c.offsetWidth;
      var pad = 12;
      if (left < tabRow.scrollLeft) {
        tabRow.scrollTo({ left: Math.max(0, left - pad), behavior: 'smooth' });
      } else if (right > tabRow.scrollLeft + tabRow.clientWidth) {
        tabRow.scrollTo({ left: right - tabRow.clientWidth + pad, behavior: 'smooth' });
      }
    }

    var painted = -1;
    function paint(n, follow) {
      chips.forEach(function (c, i) {
        var isOn = i === n;
        c.classList.toggle('is-on', isOn);
        c.setAttribute('aria-selected', isOn ? 'true' : 'false');
      });
      if (follow && n !== painted) ensureChipVisible(n);
      painted = n;
    }

    // First card belonging to each tab, for the chip taps.
    var firstOf = {};
    cards.forEach(function (c) {
      var t = c.getAttribute('data-cine-tab');
      if (!(t in firstOf)) firstOf[t] = c;
    });

    // A tap holds its chip lit until the rail has finished gliding to it.
    // Without this the scroll handler below repaints on every frame of the
    // smooth scroll, and for most of the glide the leftmost card still belongs
    // to the old tab, so the tapped chip lit, went dark again and only came
    // back when the rail landed. That read as a delay between tap and
    // highlight (Guneet, 2026-09-22). The hold ends when the rail stops (no
    // scroll event for 150ms, or `scrollend`), when the shopper takes the rail
    // themselves, or after 1.5s at the outside.
    var held = -1, holdIdle = 0, holdCap = 0;
    function release() {
      held = -1;
      clearTimeout(holdIdle); clearTimeout(holdCap);
    }
    function hold(n) {
      held = n;
      clearTimeout(holdIdle); clearTimeout(holdCap);
      holdIdle = setTimeout(release, 150);
      holdCap = setTimeout(release, 1500);
    }
    on(track, 'scrollend', function () { if (held >= 0) release(); });
    on(track, 'touchstart', release, { passive: true });
    on(track, 'wheel', release, { passive: true });
    off.push(release);

    // A tapped chip scrolls the strip so the NEXT chip peeks in at the right
    // edge (~40% of it, 32px at least), which tells a shopper there are more
    // tabs (Guneet, 2026-09-22). scrollIntoView 'nearest' did nothing when the
    // tapped chip was already in view, so the strip never hinted at the rest.
    // The tapped chip always ends fully visible; the next chip's peek wins over
    // a matching peek of the previous one on the left; the last chip is shown
    // whole with its end padding. Positions are in the strip's own scroll
    // coordinates, and a strip that does not overflow (desktop) never moves.
    function peekChips(i) {
      var max = tabRow.scrollWidth - tabRow.clientWidth;
      if (max <= 1) return;
      var rr = tabRow.getBoundingClientRect();
      function span(el) {
        var r = el.getBoundingClientRect();
        var l = r.left - rr.left + tabRow.scrollLeft;
        return { l: l, r: l + r.width, w: r.width };
      }
      var view = tabRow.clientWidth;
      var cur = span(chips[i]);
      var padR = parseFloat(getComputedStyle(tabRow).paddingRight) || 0;
      var padL = parseFloat(getComputedStyle(tabRow).paddingLeft) || 0;
      var peekOf = function (w) { return Math.min(w, Math.max(32, w * 0.4)); };
      var target;
      if (i === chips.length - 1) {
        // the last one: just enough to show it whole, end padding included
        target = Math.max(tabRow.scrollLeft, cur.r + padR - view);
        if (cur.l - padL < target) target = cur.l - padL;
      } else {
        var nx = span(chips[i + 1]);
        // right edge of the view lands ~40% into the next chip
        target = nx.l + peekOf(nx.w) - view;
        // Near the left edge with a chip before it: pull back to leave that one
        // a peek too, but never below the right-hand peek's 32px minimum, and
        // never so far that the tapped chip is cut on the right.
        if (i > 0) {
          var pv = span(chips[i - 1]);
          var leftPeek = pv.r - peekOf(pv.w);
          if (target > leftPeek) {
            target = Math.max(leftPeek, nx.l + Math.min(32, nx.w) - view, cur.r - view);
          }
        }
        // a very wide chip: keep its left edge on screen, whatever else gives
        if (target > cur.l - padL) target = cur.l - padL;
      }
      target = Math.max(0, Math.min(max, Math.round(target)));
      if (Math.abs(target - tabRow.scrollLeft) < 1) return;
      var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      tabRow.scrollTo({ left: target, behavior: still ? 'auto' : 'smooth' });
    }

    chips.forEach(function (c, i) {
      on(c, 'click', function () {
        paint(i);
        var target = firstOf[String(i)];
        if (!target) return;
        var left = target.offsetLeft - cards[0].offsetLeft;
        if (Math.abs(track.scrollLeft - left) > 1) hold(i);
        // Tapping a chip is the only thing that moves the strip.
        peekChips(i);
        track.scrollTo({ left: left, behavior: 'smooth' });
      });
    });

    var raf = 0;
    on(track, 'scroll', function () {
      if (held >= 0) {
        // still gliding to the tapped chip: keep it lit, push the idle timer on
        clearTimeout(holdIdle);
        holdIdle = setTimeout(release, 150);
        return;
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        // The active tab is whichever tab owns the leftmost card still on screen.
        var edge = track.scrollLeft + 2;
        var cur = cards[0];
        for (var i = 0; i < cards.length; i++) {
          var left = cards[i].offsetLeft - cards[0].offsetLeft;
          var right = left + cards[i].offsetWidth;
          if (right > edge) { cur = cards[i]; break; }
        }
        paint(parseInt(cur.getAttribute('data-cine-tab'), 10) || 0, true);
      });
    }, { passive: true });
    off.push(function () { cancelAnimationFrame(raf); });
  }

  function setupAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches('[data-cine-brand]')) setup(scope);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-cine-brand]'), setup);
  }
  function teardownAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-cine-brand]'), function (root) {
      if (root.__nbCineBrand) root.__nbCineBrand.destroy();
    });
  }

  /* ---- deferred cards (mobile audit P1.4, 2026-09-22) ----
     The page ships only the first tab's cards in full. Every later tab's card
     is a stand-in ([data-cine-ph]) with the real card's box: the same outer
     element, an empty photo frame, the real title link and price, and a
     button of the same class. That was ~100 of the 128 cards on the homepage.

     Each stand-in is filled with the real card's contents, fetched through the
     Section Rendering API (?sections=, up to five chapters a request; the
     section renders in full there because section.index is nil). The outer
     element is kept, not replaced, so the card list setup() holds, the rail's
     scroll position and the chips all carry on untouched.

     When: as soon as a chapter is within one and a half screens of view, on
     the first touch or tap inside a chapter, and for everything left once the
     browser is idle after load -- so a fast swipe down the deck never lands
     on a stand-in. A failed request is retried (1.5s, 4s, 10s), and again on
     the next touch in that chapter. */
  var lazyBusy = [], lazyAll = false;
  function lazyRoots() {
    return Array.prototype.filter.call(document.querySelectorAll('[data-cine-brand]'), function (r) {
      return r.querySelector('[data-cine-ph]') && r.id.indexOf('cine-') === 0;
    });
  }
  function attach(scope, root) {
    // The chapter's pictures may already have been attached by the deck
    // (newbrand-cine-chrome hydrate); attach these the same way if so.
    if (root.getAttribute('data-cine-on') !== '1') return;
    // Backgrounds too: the whole-chapter swap below brings a fresh, bare
    // .cine-bed, which lost the house photo (audit 2026-09-24).
    Array.prototype.forEach.call(scope.querySelectorAll('[data-cine-bed],[data-cine-bg]'), function (el) {
      var u = el.getAttribute('data-cine-bed') || el.getAttribute('data-cine-bg');
      if (u) el.style.backgroundImage = 'url(' + u + ')';
    });
    Array.prototype.forEach.call(scope.querySelectorAll('img[data-cine-src]'), function (img) {
      var ss = img.getAttribute('data-cine-srcset');
      if (ss) img.setAttribute('srcset', ss);
      img.setAttribute('src', img.getAttribute('data-cine-src'));
    });
  }
  function fillFrom(root, html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    var froot = tpl.content.querySelector('[data-cine-brand]');
    // An answer that came back as stand-ins again is no use.
    if (!froot || froot.querySelector('[data-cine-ph]')) return false;
    var fresh = froot.querySelectorAll('[data-cine-track] > [data-cine-tab]');
    var mine = root.querySelectorAll('[data-cine-track] > [data-cine-tab]');
    var same = fresh.length === mine.length;
    for (var j = 0; same && j < mine.length; j++) {
      if (fresh[j].getAttribute('data-cine-tab') !== mine[j].getAttribute('data-cine-tab')) same = false;
    }
    if (!same) {
      // The house's collections changed between the page and the fetch (the
      // nightly re-rank, a product going out of stock): take the whole fresh
      // chapter and set it up again rather than mixing the two.
      if (root.__nbCineBrand) root.__nbCineBrand.destroy();
      var all = document.importNode(froot, true);
      attach(all, root);
      root.replaceChildren.apply(root, Array.prototype.slice.call(all.childNodes));
      setup(root);
      return true;
    }
    for (var i = 0; i < mine.length; i++) {
      var ph = mine[i];
      if (!ph.hasAttribute('data-cine-ph')) continue;
      var kids = document.importNode(fresh[i], true);
      attach(kids, root);
      ph.replaceChildren.apply(ph, Array.prototype.slice.call(kids.childNodes));
      ph.removeAttribute('data-cine-ph');
    }
    return true;
  }
  function lazyLoad(first, attempt) {
    if (!first || lazyBusy.indexOf(first) > -1 || !first.querySelector('[data-cine-ph]')) return;
    // this chapter plus the next ones still waiting, five to a request
    var batch = [first].concat(lazyRoots().filter(function (r) { return r !== first && lazyBusy.indexOf(r) < 0; })).slice(0, 5);
    batch.forEach(function (r) { lazyBusy.push(r); });
    var ids = batch.map(function (r) { return r.id.slice(5); });
    function done() { lazyBusy = lazyBusy.filter(function (r) { return batch.indexOf(r) < 0; }); }
    function retry() {
      done();
      var n = (attempt || 0) + 1;
      if (n > 3) return; // the next touch in the chapter asks again
      setTimeout(function () { batch.forEach(function (r) { lazyLoad(r, n); }); }, [0, 1500, 4000, 10000][n]);
    }
    fetch(location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error(res.status); return res.json(); })
      .then(function (json) {
        var failed = false;
        batch.forEach(function (r, i) {
          if (!(json && json[ids[i]] && fillFrom(r, json[ids[i]]))) failed = true;
        });
        if (failed) { retry(); return; }
        done();
        if (lazyAll) { var next = lazyRoots()[0]; if (next) lazyLoad(next, 0); }
      })
      .catch(retry);
  }
  function lazyStart() {
    var roots = lazyRoots();
    if (!roots.length || !window.fetch || !('content' in document.createElement('template'))) return;
    roots.forEach(function (r) {
      r.addEventListener('touchstart', function () { lazyLoad(r, 0); }, { passive: true });
      r.addEventListener('pointerdown', function () { lazyLoad(r, 0); });
    });
    // The scroller is .page-wrapper from 990px up and the document below it
    // (same test as the deck's hostFor); rebuilt when the width crosses 990.
    var io = null, wide = null;
    function observe() {
      var w = document.querySelector('.page-wrapper');
      var isWide = !!(w && window.innerWidth >= 990 && getComputedStyle(w).overflowY === 'auto');
      if (!('IntersectionObserver' in window) || isWide === wide) return;
      wide = isWide;
      if (io) io.disconnect();
      io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); lazyLoad(e.target, 0); } });
      }, { root: isWide ? w : null, rootMargin: '150% 0px' });
      lazyRoots().forEach(function (r) { io.observe(r); });
    }
    observe();
    window.addEventListener('resize', observe, { passive: true });
    function idle() {
      var go = function () { lazyAll = true; var r = lazyRoots()[0]; if (r) lazyLoad(r, 0); };
      if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 2500 }); else setTimeout(go, 600);
    }
    if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle);
  }

  setupAll(document);
  lazyStart();
  document.addEventListener('shopify:section:load', function (e) { setupAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardownAll(e.target); });
})();
